/**
 * Lays out the body of a long marketing page as a list of titled sections: stacked one after
 * another from the `lg` breakpoint up, and as a collapsible accordion (one tab per section) below
 * it. It also handles deep links: when the URL has a `#hash`, it opens the matching accordion tab
 * on mobile and scrolls the matching section into view.
 *
 * Used by 77 pages and page components, for example `pages/why-ryerson/index.tsx`,
 * `pages/about-us/index.tsx`, `components/Solutions/BasePage/BasePage.tsx`,
 * `components/BarTubeStructuralProcessing/BasePage/BasePage.tsx` and the Contentful product pages
 * (`components/Contentful/Product.tsx`, `SubProduct.tsx`).
 * `src/unit-test/Capabilities/Capability.spec.tsx`, `src/unit-test/Industries/Industries.spec.tsx`
 * and `src/unit-test/Solutions/Solution.spec.tsx` replace it with a `jest.mock` stub. The
 * `src/unit-test/AboutUs/` specs `AboutUs.spec.tsx`, `History.spec.tsx`, `NewsEvents.spec.tsx` and
 * `CareersVariant.spec.tsx`, and `src/unit-test/Industries/Index.spec.tsx`, render pages that
 * render it unmocked. They are render-only smoke tests, and they rely on the `window.scrollTo`
 * stub in `src/jest/test-window.js`.
 *
 * Rules a change must keep:
 * - **A section's anchor is its title slugified:** lower-cased, with each run of whitespace
 *   replaced by `-` ("Frequently Asked Questions" → `frequently-asked-questions`). Pages build
 *   their `SubNavigation` items with the same expression on the same copy (for example
 *   `pages/metal-solutions/capabilities/plate-processing/burning/index.tsx` → `subNav`), so the
 *   two must change together. Titles come from per-language copy, so the anchor differs by
 *   language.
 * - `hide` removes a section from the mobile accordion only; desktop still renders it. Pages use
 *   it for a section they render on their own below `lg`, typically the Contact Us block
 *   (`pages/why-ryerson/customized-metal-solutions/index.tsx`).
 * - The component reads `window` during render. That is safe only because every page renders in
 *   the browser after mount (`components/NextLayouts/RootLayout.tsx` → `HasMounted`).
 */
import React from "react";
import styled from "@emotion/styled";
import { ContentSection } from "@ryerson/frontend.layout";
import { Accordion, AccordionTab } from "@ryerson/frontend.expansion";
import { useTheme } from "@ryerson/frontend.theme";
import { css } from "@emotion/react";
import { Media } from "@ryerson/frontend.next";

/** Props for {@link AllSections}. */
export interface AllSectionsProps {
	/** The page's sections, in display order. */
	sections: Sections[];
	/** Whether several mobile accordion tabs may be open at once. Defaults to `true`. */
	accordionMultiple?: boolean;
}

/** One titled section of a page. */
export interface Sections {
	/** Leaves the section out of the mobile accordion; desktop still renders it. */
	hide?: boolean;
	/** Mobile tab header, and the source of the section's anchor id (see the file header). */
	title: string;
	/** The rendered section body. */
	cmp: any;
	/** Mobile only: shifts the tab content down by this many pixels (CSS `top`). */
	topOffset?: number;
	/**
	 * Mobile only: passed to `AccordionTab` as the open tab's maximum height (for example
	 * `"3000px"`). Long legal pages pass much more
	 * (`TermsAndConditionsLandingRyerson.tsx`: `"35000px"`).
	 */
	maxHeight?: string;
}

/**
 * Scrolls to the element whose id is the URL hash, 110 px above it so the sticky header doesn't
 * cover it. Runs once on mount at both breakpoints.
 */
const scrollToSection = () => {
	if (typeof window !== "undefined") {
		const hash = window?.location?.hash.replace("#", "");
		if (hash) {
			const yOffset = -110;
			const section = document.getElementById(hash);
			if (section) {
				const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
				window.scrollTo({ top: y, behavior: "smooth" });
			}
		}
	}
};

/**
 * Renders the page sections as a mobile accordion and a desktop stack, and follows a `#hash` deep
 * link to its section. See the file header for the anchor rule and `hide`.
 */
export const AllSections: React.FC<AllSectionsProps> = ({ sections, accordionMultiple = true }) => {
	const { theme } = useTheme();

	// Declared inside the component, so it is a new styled component type on every render and the
	// mobile tab contents remount whenever AllSections re-renders.
	const ContentDiv = styled.div`
		background-color: ${theme.colors.primary.white};
		padding: 0;
		height: auto;
		position: relative;
		${(props: any) => {
			const { top = 0 } = props;
			return css`
				top: ${top}px;
			`;
		}}
	`;

	// One anchor id per section, indexed like `sections` (hidden sections included).
	const sectionId = sections.flatMap((section) => {
		return section.title.toLowerCase().replace(/\s+/g, "-");
	});

	type IdAndReference = {
		id: string;
		ref: React.RefObject<any>;
	};

	// A ref per section, also indexed like `sections`. On mobile it is attached to the tab header
	// (clicking it toggles the tab); on desktop to the section's wrapper `div`.
	let idsAndReferences: IdAndReference[] = [];
	sectionId.forEach((id) => {
		idsAndReferences.push({
			id: id,
			ref: React.createRef(),
		});
	});

	React.useEffect(() => {
		scrollToSection();
	}, []);

	// Deep-link handling. Below `lg`, the hash may name a section or any element inside one: the
	// first tab whose content contains that id is opened by clicking its header, then the element
	// is scrolled to once the tab has had 150 ms to expand. From `lg` up, the hash must equal a
	// section id exactly. The dependency reads `window.location.hash` at render time, so the
	// effect re-runs when a re-render happens after the hash changed; a hash change on its own
	// doesn't re-render.
	React.useEffect(() => {
		if (typeof window !== "undefined") {
			let breakPoint = parseInt(theme.breakpoints.lg.replace("px", ""));
			if (window.innerWidth < breakPoint) {
				const hash = window?.location?.hash.replace("#", "");
				if (hash) {
					let scrollTarget: Element | null = null;
					const yOffset = -110;

					idsAndReferences.some((idAndRef) => {
						// check if the section has a section with the hash. If so expand accordion and set scroll target then scroll to it after wait
						if (idAndRef.ref.current?.parentElement?.querySelector(`#${hash}`)) {
							scrollTarget = document.getElementById(hash);
							idAndRef.ref.current.click();
							setTimeout(() => {
								if (scrollTarget) {
									const y =
										scrollTarget.getBoundingClientRect().top +
										window.pageYOffset +
										yOffset;
									window.scrollTo({ top: y, behavior: "smooth" });
								}
							}, 150);
							return true;
						}
						return false;
					});
				}
			} else {
				// Desktop: the ref is the plain wrapper `div`, so the click has no tab to toggle.
				idsAndReferences.forEach((idAndRef) => {
					if (idAndRef.id === window?.location?.hash.replace("#", "")) {
						idAndRef.ref.current.click();
						setTimeout(() => {
							const yOffset = -110;
							const section = idAndRef.ref.current;
							if (section) {
								const y =
									section.getBoundingClientRect().top +
									window.pageYOffset +
									yOffset;
								window.scrollTo({ top: y });
							}
						}, 1);
					}
				});
			}
		}
	}, [window.location.hash]);

	return (
		<>
			<Media lessThan="lg">
				{/* `defaultOpen` and the `[index]` lookups below use positions in the unfiltered
				    `sections`, while `index` counts only visible sections. When a hidden section
				    comes before a visible one, that visible tab gets the id and ref of whichever
				    section sits at its visible position in the full list. */}
				<ContentSection>
					<Accordion
						variant="line"
						multiple={accordionMultiple}
						defaultOpen={
							typeof window !== "undefined"
								? sectionId.indexOf(window?.location?.hash.replace("#", ""))
								: -1
						}
					>
						{sections
							.filter((section) => !section.hide)
							.map((section: Sections, index: number) => (
								<AccordionTab
									key={`tab-${index}`}
									header={section.title}
									circled={true}
									tabHeaderRef={idsAndReferences[index].ref}
									maxHeight={section.maxHeight}
								>
									<ContentDiv
										id={sectionId[index]}
										className={"tab-content"}
										{...{ top: section.topOffset || 0 }}
									>
										{section.cmp}
									</ContentDiv>
								</AccordionTab>
							))}
					</Accordion>
				</ContentSection>
			</Media>
			<Media greaterThanOrEqual="lg">
				{sections.map((section: any, index: number) => (
					<div
						id={sectionId[index]}
						key={`${section.title}`}
						ref={idsAndReferences[index].ref}
					>
						{section.cmp}
					</div>
				))}
			</Media>
		</>
	);
};
